import { calcCashflowModelApi } from 'api/cashflowModelApi';
import { getTearsheetCashflowScenarioApi } from 'api/tearsheetApi';
import Card from 'components/card/Card';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ResultsTable from './ResultsTable';
import { normalizeStoredCashflowResult } from './normalizeStoredCashflowResult';
import { buildCashflowDownloadFilename } from './buildCashflowDownloadFilename';

import { DownloadIcon } from '@chakra-ui/icons';
import { Box, Button, Divider, Flex, useToast } from '@chakra-ui/react';
import { CSVLink } from 'react-csv';
import CashflowModelInputs from './CashflowModelInputs';
import CohortBreakdownCsv from './CohortBreakdownCsv';
import RepaymentsPerCohortCsv from './RepaymentsPerCohortCsv';
import DetailsCsv from './DetailsCsv';
import ResultsKpis from './ResultsKpis';

const CashflowModel = () => {
  const [searchParams] = useSearchParams();
  const scenarioId = searchParams.get('scenarioId');
  const customerId = searchParams.get('customerId');

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState();
  const [csvData, setCsvData] = useState([]);
  const [mainCsvFilename, setMainCsvFilename] = useState('cashflow-model.csv');

  const refModelResultsTable = useRef(null);
  const refModelInputs = useRef(null);

  const toast = useToast();

  const resolveDownloadFilename = useCallback((baseName) => {
    const ctx = refModelInputs.current?.getDownloadContext?.() ?? {};
    return buildCashflowDownloadFilename({
      customerName: ctx.customerName,
      scenarioLabel: ctx.scenarioLabel,
      baseName,
    });
  }, []);

  const handleCustomerChange = useCallback(() => {
    setResults(null);
  }, []);

  useEffect(() => {
    if (!scenarioId || !customerId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const doc = await getTearsheetCashflowScenarioApi({
          customerId,
          scenarioId,
        });
        const rawReq = doc.request;
        const data =
          rawReq == null || rawReq === ''
            ? null
            : typeof rawReq === 'string'
              ? JSON.parse(rawReq)
              : rawReq;
        let _results = null;
        const rawRes = doc.result;
        if (rawRes != null && rawRes !== '') {
          const parsed =
            typeof rawRes === 'string' ? JSON.parse(rawRes) : rawRes;
          _results = normalizeStoredCashflowResult(parsed);
        }
        if (!_results && data) {
          _results = await calcCashflowModelApi(data);
        }
        if (cancelled) {
          return;
        }
        if (_results) {
          setResults(_results);
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load tearsheet cashflow scenario.',
            status: 'error',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per scenario query
  }, [scenarioId, customerId]);

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      const data = { ...formData };
      if (data.isTrueUp && !!data.numberOfRepayments/* && data.numberOfRepayments > 0*/) {
        const trueUp = []
        for (let i = 0; i < data.numberOfRepayments; i++) {
          trueUp.push(data[`trueUpPayment${i}`] || 0);
          delete data[`trueUpPayment${i}`]
        }
        data.trueUp = trueUp;
      }
      // Map feeMode + feeOrXirrValue to backend format
      const isXirrMode = data.feeMode === 'xirr';
      data.isXirrMode = isXirrMode;
      data.fee = isXirrMode ? 0 : (data.feeOrXirrValue ?? 0);
      data.xirrPercent = isXirrMode ? (data.feeOrXirrValue ?? 0) : 0;
      delete data.feeMode;
      delete data.feeOrXirrValue;
      const _results = await calcCashflowModelApi(data);
      setResults(_results);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to calculate model.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCsvData = () => {
    setMainCsvFilename(resolveDownloadFilename('cashflow-model'));
    const modelCsv = refModelResultsTable.current?.generateCsvData?.() ?? [];
    const inputsCsv = refModelInputs.current?.generateCsvData?.() ?? [];
    const _csvData = [...modelCsv, [], [], ...inputsCsv];
    setCsvData(_csvData);
  };

  return (
    <Box minW={'container.md'} maxW={'max'}>
      <OverlaySpinnerWrapper show={loading} isFixed={true}>
        <Card>
          <CashflowModelInputs
            onSubmit={onSubmit}
            modelResults={results?.items}
            roasVector={results?.roas_100_avg_6}
            onCustomerChange={handleCustomerChange}
            ref={refModelInputs}
          />

          {results && (
            <Box mt={10}>
              <Divider borderColor={'secondaryGray.900'} />
              <Box mt={10}>
                <Box mt={5}>
                  <ResultsKpis kpis={results.kpis} />
                </Box>
                <Box mt={10}>
                  <ResultsTable
                    modelResults={results.items}
                    cohortsRawData={results.cohorts}
                    roasVector={results.roas_100_avg_6}
                    kpiVectors={results.kpis?.vectors}
                    ref={refModelResultsTable}
                  />
                </Box>
                <Box mt={10}>
                  <Flex mt={10} gap={5}>
                    <Button variant={'outline'} leftIcon={<DownloadIcon />}>
                      <CSVLink
                        data={csvData}
                        filename={mainCsvFilename}
                        onClick={generateCsvData}
                        asyncOnClick={true}
                      >
                        Download CSV
                      </CSVLink>
                    </Button>
                    <DetailsCsv
                      cohortsRawData={results.cohorts}
                      roasVector={results.roas_100_avg_6}
                      resolveDownloadFilename={resolveDownloadFilename}
                    />
                    <CohortBreakdownCsv
                      cohortRevenueBreakdown={results.cohort_revenue_breakdown}
                      modelResults={results.items}
                      resolveDownloadFilename={resolveDownloadFilename}
                    />
                    <RepaymentsPerCohortCsv
                      modelResults={results}
                      resolveDownloadFilename={resolveDownloadFilename}
                    />
                  </Flex>
                </Box>
              </Box>
            </Box>
          )}
        </Card>
      </OverlaySpinnerWrapper>
    </Box>
  );
};

export default CashflowModel;
